import React, { useState, useEffect, useRef } from "react";
import { apiAxios as axios } from "../../utils/apiClient.js";
import { useParams, Link } from "react-router-dom";
import { decryptData } from "../../utils/encryption";
import { url } from "../../Constant/TextConstant";
import { useAuth } from "../../utils/AuthContext";
import { Helmet } from "react-helmet";
import {
  fetchUniqueIds,
  sendFCMNotification,
} from "../../Services/NotificationServices";
import {
  Upload,
  Plus,
  Check,
  MapPin,
  Loader2,
  RefreshCw,
  ChevronDown,
} from "lucide-react";
import styled, { keyframes, css } from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Header from "../Header";
import Footer from "../Footer/Footer";
import { useUserLocation } from "../../utils/useUserLocation";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(74, 172, 238, 0.4); }
  70% { box-shadow: 0 0 0 15px rgba(74, 172, 238, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 172, 238, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Styled Components
const BulkOrderContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  animation: ${fadeIn} 0.6s ease-out;
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e2e8f0;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #2d3748;
  margin: 0;
  background: linear-gradient(to right, #4a6bff, #6b5bff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const RefreshButton = styled.button`
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 0.5rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: #edf2f7;
    transform: rotate(90deg);
  }
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const UploadContainer = styled.div`
  border: 2px dashed #cbd5e0;
  border-radius: 12px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;
  background: ${(props) => (props.hasImage ? "transparent" : "#f8fafc")};

  &:hover {
    border-color: #4a6bff;
    background: ${(props) => (props.hasImage ? "transparent" : "#f0f4f8")};
  }
`;

const UploadIcon = styled(Upload)`
  color: #4a6bff;
  margin-bottom: 1rem;
`;

const UploadText = styled.p`
  color: #4a5568;
  margin: 0;
  font-size: 1rem;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  object-fit: contain;
`;

const HiddenInput = styled.input`
  display: none;
`;

const DescriptionTextarea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;

  &:focus {
    border-color: #4a6bff;
    box-shadow: 0 0 0 3px rgba(74, 107, 255, 0.2);
    outline: none;
  }

  &::placeholder {
    color: #a0aec0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  color: #2d3748;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const AddressList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const AddressCard = styled.div`
  border: 2px solid ${(props) => (props.selected ? "#4a6bff" : "#e2e8f0")};
  border-radius: 8px;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  background: ${(props) => (props.selected ? "#f8faff" : "white")};

  &:hover {
    border-color: ${(props) => (props.selected ? "#4a6bff" : "#cbd5e0")};
  }
`;

const AddressName = styled.p`
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: #2d3748;
`;

const AddressText = styled.p`
  margin: 0.25rem 0;
  color: #4a5568;
  font-size: 0.9rem;
`;

const CheckIcon = styled(Check)`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: #4a6bff;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: linear-gradient(to right, #4a6bff, #6b5bff);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    background: linear-gradient(to right, #3a5bef, #5a4bff);
  }

  &:disabled {
    background: #cbd5e0;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SpinIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 2rem;
  animation: ${fadeIn} 0.6s ease-out;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  background-color: #48bb78;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const TrackOrderButton = styled(Link)`
  display: inline-block;
  padding: 1rem 2rem;
  background: linear-gradient(to right, #48bb78, #38a169);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 2rem;
  text-decoration: none;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: ${pulse} 2s infinite;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
    background: linear-gradient(to right, #3cad6d, #2d8e59);
  }
`;

// State codes mapping
const stateCodes = {
  "Andhra Pradesh": "AP",
  Bihar: "BR",
  // Add all other states as needed
};

const BulkOrderComponent = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [description, setDescription] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [otp, setOtp] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderNumber, setOrderNumber] = useState("");
  const fileInputRef = useRef(null);
  const { encryptedProviderMobile } = useParams();
  const { currentUser, currentUserName } = useAuth();
  const storeMobile = decryptData(encryptedProviderMobile);
  const baseAPIUrl = url.baseAPIUrl;

  // Replace the existing location state with the useUserLocation hook
  const {
    location: currentLocation,
    error: locationError,
    isLoading: isLocationLoading,
    updateLocation,
  } = useUserLocation();

  const [showModal, setShowModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(() => {
    return localStorage.getItem("selectedCategory") || "products";
  });

    useEffect(() => {
        window.scrollTo(0, 0);
      }, []);

  // Update localStorage whenever selectedCategory changes
  useEffect(() => {
    localStorage.setItem("selectedCategory", selectedCategory);
  }, [selectedCategory]);

  // Handler for modal close with location update
  const handleModalClose = (shouldClose, newCoordinates = null) => {
    setShowModal(false);
    if (newCoordinates) {
      // Update location through the hook's update function
      updateLocation({
        coordinates: {
          latitude: newCoordinates.latitude,
          longitude: newCoordinates.longitude,
        },
        address: newCoordinates.address || "",
        details: newCoordinates.details || null,
      });
    }
  };

  useEffect(() => {
    fetchAddresses();
  }, [currentUser]);

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(
        `${baseAPIUrl}/api/Checkout/BindUserTblAddress?CustomerMobile=${currentUser}`
      );
      if (response.status === 200) {
        const transformedAddresses = response.data.map((address) => ({
          id: address.ID,
          registeredMobile: address.RegisteredMobile,
          name: address.Name,
          mobile: address.Mobile,
          locality: address.Locality,
          city: address.City,
          state: address.State,
          pinCode: address.PinCode,
          fullAddress: address.FullAddress,
        }));
        setAddresses(transformedAddresses);
      }
    } catch (error) {
      toast.error("Failed to fetch addresses", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleImagePick = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => setSelectedImagePreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append("file", imageFile, imageFile.name);

      const response = await axios.post(
        `${baseAPIUrl}/api/UploadImage/UploadProductImage`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      return response.data;
    } catch (error) {
      toast.error("Image upload failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return null;
    }
  };

  const generateOrderNumber = (state) => {
    const stateCode = stateCodes[state] || "IN";
    const number = `NearMe-IND-BULK_ORDER-${stateCode}${Date.now()}`;
    setOrderNumber(number);
    return number;
  };

  const generateDeliveryPin = () => {
    const pin = Math.floor(1000 + Math.random() * 9000).toString();
    setOtp(pin);
    return pin;
  };

  const submitOrderToServer = async (orderData) => {
    try {
      const response = await axios.post(
        `${baseAPIUrl}/api/ListOrder/InsertOrderList`,
        orderData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      return response;
    } catch (error) {
      return null;
    }
  };

  const handleSubmit = async () => {
    if (!selectedImage || !description) {
      toast.warn("Please fill all fields and select an image", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    if (!selectedAddress) {
      toast.warn("Please select an address", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
      return;
    }

    setIsUploading(true);

    const uploadedImageUrl = await uploadImage(selectedImage);
    if (!uploadedImageUrl) {
      setIsUploading(false);
      return;
    }

    const orderNumber = generateOrderNumber(selectedAddress.state);
    const deliveryPin = generateDeliveryPin();

    const orderData = {
      CustomerMobile: selectedAddress.registeredMobile,
      OrderNumber: orderNumber,
      Name: selectedAddress.name,
      Email: "abc@gmail.com",
      Locality: selectedAddress.locality,
      City: selectedAddress.city,
      State: selectedAddress.state,
      Pincode: selectedAddress.pinCode,
      FullAddress: selectedAddress.fullAddress,
      Image: uploadedImageUrl,
      Description: description,
      StoreMobile: storeMobile,
      DeliveryPin: deliveryPin,
      AlternateMobile: selectedAddress.mobile,
    };

    const response = await submitOrderToServer(orderData);
    setIsUploading(false);

    if (response && response.status === 200) {
      toast.success("Order Submitted Successfully!", {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Send notifications
      const uniqueIds = await fetchUniqueIds(storeMobile);
      if (uniqueIds) {
        await sendFCMNotification({
          Tokens: uniqueIds,
          Title: "New Order Placed",
          Body: `A new order has been placed by ${
            currentUserName || "Customer"
          }`,
          ScreenRoute: "/orderDetails",
        });
      }

      setOrderSuccess(true);
    } else {
      toast.error("Order Submission Failed. Please try again.", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  if (orderSuccess) {
    return (
      <>
        <Helmet>
          <title>
            {orderSuccess
              ? `Order #${orderNumber} Confirmation`
              : "Place Bulk Order"}
          </title>
          <meta
            name="description"
            content={
              orderSuccess
                ? `Your bulk order #${orderNumber} has been successfully placed. Track your order status.`
                : "Place bulk orders with multiple items. Upload product images and specify your requirements."
            }
          />
          <meta
            name="keywords"
            content="bulk order, wholesale purchase, business order, large quantity order, product sourcing"
          />
          <meta
            property="og:title"
            content={
              orderSuccess
                ? `Order Confirmation #${orderNumber}`
                : "Bulk Order Form"
            }
          />
          <meta
            property="og:description"
            content={
              orderSuccess
                ? `Your bulk order has been placed successfully. Order number: ${orderNumber}`
                : "Submit your bulk order requirements with images and specifications"
            }
          />
          <meta property="og:type" content="website" />
          {orderSuccess && (
            <meta
              property="og:url"
              content={`${window.location.origin}/order/${orderNumber}`}
            />
          )}
        </Helmet>
        <Header
          userLocation={currentLocation} // Use currentLocation from hook
          setShowModal={setShowModal}
          setUserLocation={updateLocation} // Use updateLocation from hook
          showModal={showModal}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
        <BulkOrderContainer>
          <SuccessContainer>
            <SuccessIcon>✓</SuccessIcon>
            <h1>Your Bulk Order Has Been Placed Successfully!</h1>
            <h2>Order Details</h2>
            <p>
              Order Number: <strong>{orderNumber}</strong>
            </p>
            <p>
              We've notified the store about your order. You'll receive updates
              on your order status.
            </p>
            <TrackOrderButton to="/listOrder">
              Track Your Orders
            </TrackOrderButton>
          </SuccessContainer>
        </BulkOrderContainer>
        <Footer />
        <ToastContainer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>List Order {encryptedProviderMobile}</title>
      </Helmet>
      <Header
        userLocation={currentLocation} // Use currentLocation from hook
        setShowModal={setShowModal}
        setUserLocation={updateLocation} // Use updateLocation from hook
        showModal={showModal}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />
      <BulkOrderContainer>
        <HeaderContainer>
          <h1>Bulk Order Request</h1>
          <RefreshButton onClick={fetchAddresses}>
            <RefreshCw size={20} />
          </RefreshButton>
        </HeaderContainer>

        <Section>
          <h2>Upload Product Image</h2>
          <UploadContainer
            onClick={() => fileInputRef.current.click()}
            hasImage={selectedImagePreview}
          >
            {selectedImagePreview ? (
              <PreviewImage src={selectedImagePreview} alt="Selected" />
            ) : (
              <>
                <UploadIcon size={48} />
                <UploadText>Click to upload product image</UploadText>
              </>
            )}
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImagePick}
            />
          </UploadContainer>

          <h2>Order Description</h2>
          <DescriptionTextarea
            placeholder="Enter order description (quantity, specifications, special instructions)..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Section>

        <Section>
          <h2>Select Billing Address</h2>
          <AddressList>
            {addresses.map((address) => (
              <AddressCard
                key={address.id}
                selected={selectedAddress?.id === address.id}
                onClick={() => setSelectedAddress(address)}
              >
                <AddressName>{address.name}</AddressName>
                <AddressText>
                  {address.locality}, {address.city}
                </AddressText>
                <AddressText>
                  {address.state} - {address.pinCode}
                </AddressText>
                <AddressText>Phone: {address.mobile}</AddressText>
                {selectedAddress?.id === address.id && <CheckIcon size={18} />}
              </AddressCard>
            ))}
          </AddressList>
        </Section>

        <SubmitButton onClick={handleSubmit} disabled={isUploading}>
          {isUploading ? (
            <>
              <SpinIcon size={18} />
              Submitting Order...
            </>
          ) : (
            "Submit Bulk Order"
          )}
        </SubmitButton>
      </BulkOrderContainer>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default BulkOrderComponent;
